import { ReactNode, useEffect, useState } from 'react'
import { intervalToDuration, isBefore } from 'date-fns'
import { Row } from '@tanstack/react-table'

import { Log } from '@/types/logs'
import { useInvalidateControllerLogsAfterReassigned } from '@/modules/Workstation/api/useReassignWorkstationRoute'

import tableStyles from '@/ui/Table/ui/Table.module.scss'
import styles from './Reassigned.module.scss'

import InformationIcon from '../../assets/information.svg'

interface Props {
    children: ReactNode
    currentRow: Row<Log>
    currentIndex: number
    array: Row<Log>[]
}

const CountdownTimer = ({ endTime }: { endTime?: string }) => {
    const invalidateControllerLogsAfterReassigned = useInvalidateControllerLogsAfterReassigned()

    const [timeLeft, setTimeLeft] = useState('')

    useEffect(() => {
        const end = endTime ? new Date(endTime) : new Date()
        let timer: NodeJS.Timeout | null = null
        const updateTimer = () => {
            const now = new Date()

            if (isBefore(end, now)) {
                invalidateControllerLogsAfterReassigned()
                if (timer) clearInterval(timer)
                return
            }

            const { minutes = 0, seconds = 0 } = intervalToDuration({
                start: now,
                end,
            })

            setTimeLeft(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
        }

        updateTimer()
        timer = setInterval(updateTimer, 1000)

        return () => clearInterval(timer)
    }, [endTime, invalidateControllerLogsAfterReassigned])

    return <>{timeLeft}</>
}

export const Reassigned = ({ children, currentRow, currentIndex, array }: Props) => {
    const previousLogRow = array[currentIndex - 1]

    const isReassignedHeaderVisible =
        !currentIndex || currentRow.original.reassignedRoute?.route !== previousLogRow.original.reassignedRoute?.route
    const isSeparatorVisible =
        !!currentIndex && currentRow.original.reassignedRoute?.route !== previousLogRow.original.reassignedRoute?.route
    const isLastElement = currentIndex === array.length - 1

    return (
        <>
            {isSeparatorVisible && <tr className={styles.bodySeparator} />}
            {isReassignedHeaderVisible && (
                <>
                    <tr className={styles.bodySeparator}>
                        <td className={styles.reassignedGroupHeader} colSpan={currentRow.getVisibleCells().length}>
                            <div className={styles.reassignedInfo}>
                                <div className={styles.reassignedDescription}>
                                    <InformationIcon />
                                    <p>
                                        Route {currentRow.original.reassignedRoute?.route} is being reassigned -
                                        complete these records now
                                    </p>
                                </div>

                                <p className={styles.reassignedLeftTime}>
                                    Reassigning in{' '}
                                    <span>
                                        <CountdownTimer
                                            endTime={currentRow.original.reassignedRoute?.reassignmentEndDate}
                                        />
                                    </span>
                                </p>
                            </div>
                        </td>
                    </tr>
                    <tr className={tableStyles.rowSeparator} />
                </>
            )}
            {children}
            {isLastElement && <tr className={styles.bodySeparator} />}
        </>
    )
}
